import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, toRaw, computed } from 'vue';
import { usePermissionComposable } from '@/composables/usePermission';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { defineMessages } from '@/lib/i18n';

import { Company } from '@/interfaces/vuex/company';
import { DynamicList } from '@/interfaces/model/general/dynamicList';
import { StatusId } from '@/lib/enum';
import { store } from '@/store/index';
import {
  dispatchGetCompanySdsesCount,
  dispatchGetDynamicLists,
  dispatchResumeDynamicLists,
  dispatchSuspendDynamicLists,
  dispatchDeleteDynamicLists,
  dispatchFavoriteDynamicLists,
  dispatchUnfavoriteDynamicLists,
} from '@/store/main/actionsMain';
import {
  readAssertedCompany,
  readAssertedCompanyId,
  readIsFeatureEnabled,
  readUserLocale,
} from '@/store/main/getters';
import DynamicListTable from '@/views/pages/dynamicList/DynamicListTable.vue';
import ListCreateDialog from '@/views/pages/dynamicList/ListCreateDialog.vue';
import DynamicListBottomSheet from '@/views/pages/dynamicList/DynamicListBottomSheet.vue';
import { SortState } from '@/composables/useSds';
import sharedMessages from '@/lib/shared-messages';
import { formatToDateTime } from '@/utils/datetime';


export default /*#__PURE__*/_defineComponent({
  __name: 'DynamicListPage',
  setup(__props) {

const locale = computed(() => readUserLocale(store));
const isNewChecklistUiEnabled = computed(() => {
  return (
    readIsFeatureEnabled(store)('use_new_regulatory_screening_ui') ?? false
  );
});

const m = defineMessages({
  createList: 'リスト追加',
  createChecklist: 'チェックリストを追加する',
  checklistCustomization: 'チェックリストカスタマイズ',
  noChecklistTitle: '新しいチェックリストを追加して、SDSの管理を始めましょう。',
  noChecklistDescription:
    'リストを追加すると、登録されているSDSから条件に合うものを抽出できます。',
});

const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const { isFeatureActive, COMPANY_FEATURES, generateInactiveMessage } =
  useCompanyFeatureComposable();

const nextUpdateDate = () => {
  const japanNow = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' })
  );

  japanNow.setDate(japanNow.getDate() + 1);
  japanNow.setHours(0, 30, 0, 0);

  const nextUpdateTime = new Date(
    japanNow.toLocaleString('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  );

  return formatToDateTime(nextUpdateTime.toISOString());
};

const sortState = ref<SortState>({
  updated_at: 'none',
  created_at: 'none',
});

const lastCount = ref<number>(0);
const total = ref<number>(0);
const uploadedTotal = ref<number>(0);
const page = ref<number>(1);
const limit = ref<number>(50);
const isLoadingSdses = ref<boolean>(false);

const dynamicLists = ref<Array<DynamicList>>([]);
const company = ref<Company>({ ...readAssertedCompany(store) });
const uploadingToCompletedStatusIdList = ref<Array<number>>([
  ...StatusId.uploadingToCompletedValues(),
]);
const isListCreateDialogVisible = ref(false);

const selectedDynamicLists = ref<DynamicList[]>([]);
const isFilterPanelVisible = ref(false);

const updateSelectedDynamicLists = (lists: DynamicList[]) => {
  selectedDynamicLists.value = [...lists];
};

const fetchCompanySdsesCountWithAll = async (): Promise<void> => {
  const res = await dispatchGetCompanySdsesCount(store, {
    companyId: readAssertedCompanyId(store),
    data: {
      keyword_list: [],
      status_id_list: uploadingToCompletedStatusIdList.value,
    },
  });
  if (res) {
    uploadedTotal.value = res.data.total;
  }
};

const fetchDynamicLists = async () => {
  dynamicLists.value.splice(0);

  const res = await dispatchGetDynamicLists(store, {
    page: page.value - 1,
    limit: limit.value,
    showAll: true,
  });
  if (res && res.data) {
    total.value = res.data.total;
    dynamicLists.value = [...res.data.items];
    lastCount.value = dynamicLists.value[0]?.last_count || 0;
  }
};

const showListCreateDialog = () => {
  isListCreateDialogVisible.value = true;
};

const getSelectedIds = () => selectedDynamicLists.value.map((list) => list.id);

const clearSelectionAndRefresh = async () => {
  selectedDynamicLists.value = [];
  await fetchDynamicLists();
};

const resumeDynamicLists = async () => {
  await dispatchResumeDynamicLists(store, {
    dynamicListIds: getSelectedIds(),
  });
  await clearSelectionAndRefresh();
};

const suspendDynamicLists = async () => {
  await dispatchSuspendDynamicLists(store, {
    dynamicListIds: getSelectedIds(),
  });
  await clearSelectionAndRefresh();
};

const deleteDynamicLists = async () => {
  try {
    await dispatchDeleteDynamicLists(store, {
      dynamicListIds: getSelectedIds(),
    });
    await clearSelectionAndRefresh();
  } catch (error) {
    // エラーは actionDeleteDynamicLists 内で通知されるので、ここでは何もしない
  }
};

// SE-572: お気に入り(★)の一括登録/解除
const favoriteDynamicLists = async () => {
  try {
    await dispatchFavoriteDynamicLists(store, {
      dynamicListIds: getSelectedIds(),
    });
    await clearSelectionAndRefresh();
  } catch (error) {
    // エラーは action 内で通知されるので、ここでは何もしない
  }
};

// SE-572 UX指摘①-B: 解除は確認ダイアログ廃止・即時実行。Undo(元に戻す)用に解除したidsを保持
const isUndoSnackbarVisible = ref(false);
const lastUnfavoritedIds = ref<number[]>([]);

const unfavoriteDynamicLists = async () => {
  try {
    const ids = getSelectedIds();
    await dispatchUnfavoriteDynamicLists(store, {
      dynamicListIds: ids,
    });
    lastUnfavoritedIds.value = ids;
    await clearSelectionAndRefresh();
    isUndoSnackbarVisible.value = true;
  } catch (error) {
    // エラーは action 内で通知されるので、ここでは何もしない
  }
};

const undoUnfavorite = async () => {
  isUndoSnackbarVisible.value = false;
  if (lastUnfavoritedIds.value.length === 0) return;
  try {
    // 再登録（成功時は action 側で「お気に入りに登録しました」を通知）
    await dispatchFavoriteDynamicLists(store, {
      dynamicListIds: lastUnfavoritedIds.value,
    });
    await fetchDynamicLists();
  } catch (error) {
    // エラーは action 内で通知されるので、ここでは何もしない
  }
};

const toggleFilterPanel = () => {
  isFilterPanelVisible.value = !isFilterPanelVisible.value;
};

const handleCancelFilters = () => {
  isFilterPanelVisible.value = false;
};

const applySort = () => {
  const sortColumn = Object.keys(sortState.value).find(
    (key) => sortState.value[key] !== 'none'
  );
  if (!sortColumn) return;

  const sortOrder = sortState.value[sortColumn];

  if (sortColumn && sortOrder !== 'none') {
    dynamicLists.value = dynamicLists.value.slice().sort((a, b) => {
      const valueA = new Date(toRaw(a)[sortColumn]);
      const valueB = new Date(toRaw(b)[sortColumn]);

      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else if (sortOrder === 'desc') {
        return valueA < valueB ? 1 : -1;
      }
      return 0;
    });
  }
};

const toggleSort = (column: string): void => {
  isLoadingSdses.value = true;

  try {
    sortState.value[column] =
      sortState.value[column] === 'asc' ? 'desc' : 'asc';

    Object.keys(sortState.value).forEach((key) => {
      if (key !== column) {
        sortState.value[key] = 'none';
      }
    });

    applySort();
  } catch (error) {
    console.error('ソート処理中にエラーが発生しました:', error);
  } finally {
    setTimeout(() => {
      isLoadingSdses.value = false;
    }, 500);
  }
};

onMounted(async (): Promise<void> => {
  isLoadingSdses.value = true;
  await fetchDynamicLists();
  await fetchCompanySdsesCountWithAll();
  isLoadingSdses.value = false;
});

return { __sfc: true,locale, isNewChecklistUiEnabled, m, hasPermission, P, isFeatureActive, COMPANY_FEATURES, generateInactiveMessage, nextUpdateDate, sortState, lastCount, total, uploadedTotal, page, limit, isLoadingSdses, dynamicLists, company, uploadingToCompletedStatusIdList, isListCreateDialogVisible, selectedDynamicLists, isFilterPanelVisible, updateSelectedDynamicLists, fetchCompanySdsesCountWithAll, fetchDynamicLists, showListCreateDialog, getSelectedIds, clearSelectionAndRefresh, resumeDynamicLists, suspendDynamicLists, deleteDynamicLists, favoriteDynamicLists, isUndoSnackbarVisible, lastUnfavoritedIds, unfavoriteDynamicLists, undoUnfavorite, toggleFilterPanel, handleCancelFilters, applySort, toggleSort, DynamicListTable, ListCreateDialog, DynamicListBottomSheet, sharedMessages }
}

})